import { useState, useEffect, useRef } from "react";
import { useAppTheme, THEMES, NAVY, ACCENT } from "./ThemeContext";
import { useAuth } from "./AuthContext";
import { send, remove, cachedJSON, newRef, useOutbox } from "./outbox";

const localDate = () => new Date().toLocaleDateString("sv-SE");

const API = "/api";

function fmtElapsed(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function roundToQuarter(hours) {
  return Math.round(hours * 4) / 4;
}

function loadJson(key) {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : null; } catch { return null; }
}
function saveJson(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}
function clearKey(key) {
  try { localStorage.removeItem(key); } catch {}
}

function EntryCard({ e, t, dark, onDelete, isAdmin, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [editHours, setEditHours] = useState(String(e.hours));
  const [editDesc, setEditDesc] = useState(e.description || "");
  const [editMachine, setEditMachine] = useState(e.machine_name || "");
  const [editSite, setEditSite] = useState(e.site || "");

  const handleSave = () => {
    const updates = {};
    const h = parseFloat(editHours);
    if (!isNaN(h) && h !== e.hours) updates.hours = h;
    if (editDesc !== (e.description || "")) updates.description = editDesc;
    if (editMachine !== (e.machine_name || "")) updates.machine_name = editMachine;
    if (editSite !== (e.site || "")) updates.site = editSite;
    if (Object.keys(updates).length > 0) onUpdate(e.id, updates);
    setEditing(false);
  };

  const inputStyle = {
    background: dark ? "#1a1e27" : "#f0f1f4", border: `1px solid ${dark ? "#2b303c" : "#d0d3da"}`,
    borderRadius: 6, padding: "6px 8px", color: dark ? "#e8eaef" : "#1a1f29", fontSize: 13,
    outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box",
  };

  if (editing) {
    return (
      <div style={{
        background: t.panel, border: `1px solid ${ACCENT}`, borderRadius: 10, padding: "14px 16px", marginBottom: 10,
      }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 10, color: t.muted, marginBottom: 3, fontWeight: 600 }}>MASIN</div>
            <input value={editMachine} onChange={ev => setEditMachine(ev.target.value)} style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: t.muted, marginBottom: 3, fontWeight: 600 }}>OBJEKT</div>
            <input value={editSite} onChange={ev => setEditSite(ev.target.value)} style={inputStyle} />
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "80px 1fr", gap: 8, marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 10, color: t.muted, marginBottom: 3, fontWeight: 600 }}>TUNNID</div>
            <input type="number" step="0.25" value={editHours} onChange={ev => setEditHours(ev.target.value)} style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: t.muted, marginBottom: 3, fontWeight: 600 }}>KIRJELDUS</div>
            <input value={editDesc} onChange={ev => setEditDesc(ev.target.value)} style={inputStyle} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={() => setEditing(false)} style={{
            background: dark ? "#333" : "#e0e0e0", border: "none", borderRadius: 6, padding: "6px 14px",
            color: t.text, fontSize: 12, fontWeight: 600, cursor: "pointer",
          }}>Tühista</button>
          <button onClick={handleSave} style={{
            background: ACCENT, border: "none", borderRadius: 6, padding: "6px 14px",
            color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer",
          }}>Salvesta</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: t.panel, border: `1px solid ${t.border}`, borderRadius: 10, padding: "14px 16px", marginBottom: 10,
      display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>{e.machine_name}</div>
        <div style={{ fontSize: 12, color: t.muted, marginTop: 2 }}>
          {[e.site, e.machine_type, e.operator_name].filter(Boolean).join(" · ")}
        </div>
        {e.description && (
          <div style={{ fontSize: 13, color: t.mutedLight, marginTop: 6, fontStyle: "italic" }}>{e.description}</div>
        )}
        {e.pending && (
          <div style={{ fontSize: 11, color: "#b7791f", marginTop: 6, fontWeight: 700 }}>⏳ ootab saatmist</div>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 12 }}>
        <div style={{ fontWeight: 800, fontSize: 18, color: ACCENT }}>{Number(e.hours).toFixed(1)} h</div>
        {isAdmin && !e.pending && (
          <button onClick={() => setEditing(true)} style={{
            background: "none", border: "none", color: t.mutedLight, fontSize: 14, cursor: "pointer", padding: 4,
          }} title="Muuda">✎</button>
        )}
        <button onClick={() => onDelete(e.id)} style={{
          background: "none", border: "none", color: t.mutedLight, fontSize: 16, cursor: "pointer", padding: 4,
        }}>×</button>
      </div>
    </div>
  );
}

function syncTimer(state) {
  const opts = state?.running
    ? { method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ machine_name: state.machine, site: state.site, started_at: state.startedAt }) }
    : { method: "DELETE" };
  fetch(`${API}/time/timer`, { ...opts, credentials: "include" }).catch(() => {});
}

function RunningTimers({ t, dark }) {
  const [timers, setTimers] = useState([]);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const load = () => fetch(`${API}/time/timers`, { credentials: "include" })
      .then(r => (r.ok ? r.json() : [])).then(setTimers).catch(() => {});
    load();
    const poll = setInterval(load, 30000);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, []);

  if (!timers.length) return null;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: t.muted, letterSpacing: 0.5, marginBottom: 8 }}>
        KÄIMASOLEVAD TAIMERID ({timers.length})
      </div>
      {timers.map(tm => {
        const secs = Math.max(0, Math.floor((now - new Date(tm.started_at).getTime()) / 1000));
        const started = new Date(tm.started_at).toLocaleTimeString("et-EE", { hour: "2-digit", minute: "2-digit" });
        return (
          <div key={tm.user_id} style={{
            display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
            background: dark ? "rgba(47,111,237,0.10)" : "rgba(47,111,237,0.07)", border: `1px solid ${ACCENT}44`,
            borderLeft: `3px solid ${ACCENT}`, borderRadius: 10, padding: "10px 14px", marginBottom: 8,
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: t.text }}>{tm.operator_name}</div>
              <div style={{ fontSize: 12, color: t.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {[tm.machine_name, tm.site].filter(Boolean).join(" · ")} · alates {started}
              </div>
            </div>
            <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontWeight: 800, fontSize: 16, color: ACCENT, flexShrink: 0 }}>
              {fmtElapsed(secs)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ManualHoursForm({ t, dark, machines, sites, onSaved, inputStyle }) {
  const [open, setOpen] = useState(false);
  const [operators, setOperators] = useState([]);
  const [userId, setUserId] = useState("");
  const [workDate, setWorkDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [machine, setMachine] = useState("");
  const [site, setSite] = useState("");
  const [hours, setHours] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || operators.length) return;
    fetch(`${API}/auth/users`, { credentials: "include" })
      .then(r => r.json())
      .then(users => setOperators((users || []).filter(u => u.active && u.role === "operator")))
      .catch(() => setError("Operaatorite laadimine ebaõnnestus"));
  }, [open, operators.length]);

  const hoursNum = parseFloat(hours.replace(",", "."));
  const valid = userId && workDate && machine && site && hoursNum > 0 && hoursNum <= 24 && description.trim();

  const handleSave = async () => {
    if (!valid || saving) return;
    setSaving(true); setError("");
    const ref = newRef();
    const operator = operators.find(u => String(u.id) === String(userId));
    const result = await send(`${API}/time/log`, {
      kind: "hours", meta: { ref, operator: operator?.name },
      body: { user_id: Number(userId), work_date: workDate, machine_name: machine,
              site, hours: hoursNum, description: description.trim(), client_ref: ref },
    });
    setSaving(false);
    if (result === "rejected") {
      setError("Server keeldus kirjet salvestamast. Kontrolli välju.");
      return;
    }
    setHours(""); setDescription("");
    onSaved();
  };

  const label = { fontSize: 11, color: t.muted, letterSpacing: 1, marginBottom: 6, fontWeight: 700 };

  return (
    <div style={{ border: `1px solid ${t.border}`, borderRadius: 12, marginBottom: 24, background: t.panel }}>
      <button onClick={() => setOpen(o => !o)} style={{
        all: "unset", boxSizing: "border-box", cursor: "pointer", width: "100%", padding: "14px 16px",
        display: "flex", justifyContent: "space-between", alignItems: "center", color: t.text, fontWeight: 700, fontSize: 14,
      }}>
        <span>Lisa tunnid operaatorile</span>
        <span style={{ color: t.muted, fontSize: 12 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <div style={label}>OPERAATOR</div>
            <select value={userId} onChange={e => setUserId(e.target.value)} style={inputStyle}>
              <option value="">— Vali operaator —</option>
              {operators.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <div style={label}>KUUPÄEV</div>
              <input type="date" value={workDate} max={new Date().toISOString().slice(0, 10)}
                onChange={e => setWorkDate(e.target.value)} style={{ ...inputStyle, colorScheme: dark ? "dark" : "light" }} />
            </div>
            <div>
              <div style={label}>TUNNID</div>
              <input type="number" inputMode="decimal" min="0.25" max="24" step="0.25" value={hours}
                onChange={e => setHours(e.target.value)} placeholder="nt 8" style={inputStyle} />
            </div>
          </div>
          <div>
            <div style={label}>MASIN</div>
            <select value={machine} onChange={e => setMachine(e.target.value)} style={inputStyle}>
              <option value="">— Vali masin —</option>
              <option value="Käsitöö">Käsitöö (ilma masinata)</option>
              {machines.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <div style={label}>OBJEKT</div>
            <select value={site} onChange={e => setSite(e.target.value)} style={inputStyle}>
              <option value="">— Vali objekt —</option>
              {sites.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <div style={label}>KIRJELDUS *</div>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}
              placeholder="Mis tööd tehti?" style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} />
          </div>
          {error && <div style={{ color: "#e53e3e", fontSize: 13 }}>{error}</div>}
          <button onClick={handleSave} disabled={!valid || saving} style={{
            padding: 14, border: "none", borderRadius: 10, fontWeight: 800, fontSize: 15, color: "#fff",
            background: valid && !saving ? ACCENT : (dark ? "#2b303c" : "#d0d3da"),
            cursor: valid && !saving ? "pointer" : "not-allowed",
          }}>{saving ? "Salvestan..." : "Salvesta tunnid"}</button>
        </div>
      )}
    </div>
  );
}

export default function TimeLogger() {
  const themeCtx = useAppTheme();
  const authCtx = useAuth();
  const dark = themeCtx ? themeCtx.dark : true;
  const t = themeCtx ? themeCtx.t : THEMES.dark;

  const [timerState, setTimerState] = useState(() => loadJson("timer_state"));
  const [pendingEntry, setPendingEntry] = useState(() => loadJson("timer_pending"));
  const [elapsed, setElapsed] = useState(0);

  const [selectedMachine, setSelectedMachine] = useState("");
  const [selectedSite, setSelectedSite] = useState("");
  const [showNewMachine, setShowNewMachine] = useState(false);
  const [newMachineName, setNewMachineName] = useState("");

  const [description, setDescription] = useState("");
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  const [machines, setMachines] = useState([]);
  const [sites, setSites] = useState([]);
  const [entries, setEntries] = useState([]);
  const [entryRange, setEntryRange] = useState("today");

  const intervalRef = useRef(null);

  useEffect(() => {
    cachedJSON(`${API}/time/suggestions`).then(d => setMachines(d.machines || [])).catch(() => {});
    cachedJSON(`${API}/sites`).then(setSites).catch(() => {});
  }, []);

  const loadEntries = (range) => {
    const today = new Date();
    let url = `${API}/time/today`;
    if (range === "week") {
      const from = new Date(today); from.setDate(from.getDate() - 7);
      url += `?date_from=${from.toISOString().slice(0,10)}&date_to=${today.toISOString().slice(0,10)}`;
    } else if (range === "month") {
      const from = new Date(today); from.setDate(from.getDate() - 30);
      url += `?date_from=${from.toISOString().slice(0,10)}&date_to=${today.toISOString().slice(0,10)}`;
    }
    cachedJSON(url, `time_entries:${range}:${localDate()}`).then(setEntries).catch(() => {});
  };

  useEffect(() => {
    loadEntries(entryRange);
  }, [entryRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const outbox = useOutbox();
  const pendingHours = outbox.items.filter(i => i.kind === "hours").map(i => ({
    id: i.meta.ref, pending: true, machine_name: i.body.machine_name, site: i.body.site,
    work_date: i.body.work_date, hours: i.body.hours, description: i.body.description,
    operator_name: i.meta.operator || authCtx?.user?.name,
  })).filter(e => entryRange !== "today" || e.work_date === localDate());
  const shownEntries = [...pendingHours, ...entries];

  const pendingCount = outbox.count;
  const prevPending = useRef(pendingCount);
  useEffect(() => {
    // Once the outbox drains, the server has everything: show its version of the list.
    if (prevPending.current > 0 && pendingCount === 0) loadEntries(entryRange);
    prevPending.current = pendingCount;
  }, [pendingCount]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const resync = () => syncTimer(loadJson("timer_state"));
    window.addEventListener("online", resync);
    return () => window.removeEventListener("online", resync);
  }, []);

  useEffect(() => {
    // Re-register on load so timers started offline or before the server knew about timers show up.
    syncTimer(loadJson("timer_state"));
  }, []);

  useEffect(() => {
    if (pendingEntry) setShowSubmitModal(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!timerState?.running) { clearInterval(intervalRef.current); return; }
    const tick = () => {
      const started = new Date(timerState.startedAt).getTime();
      setElapsed(Math.floor((Date.now() - started) / 1000));
    };
    tick();
    intervalRef.current = setInterval(tick, 1000);
    return () => clearInterval(intervalRef.current);
  }, [timerState?.running, timerState?.startedAt]);

  const handleStart = () => {
    const state = { running: true, startedAt: new Date().toISOString(), machine: selectedMachine, site: selectedSite };
    setTimerState(state);
    saveJson("timer_state", state);
    syncTimer(state);
    setElapsed(0);
  };

  const handleStop = () => {
    const started = new Date(timerState.startedAt).getTime();
    const elapsedSec = Math.floor((Date.now() - started) / 1000);
    const hours = roundToQuarter(elapsedSec / 3600);
    const entry = {
      machine: timerState.machine,
      site: timerState.site,
      hours: Math.max(hours, 0.25),
      elapsedSec,
      stoppedAt: new Date().toISOString(),
    };
    setPendingEntry(entry);
    saveJson("timer_pending", entry);
    setTimerState(null);
    clearKey("timer_state");
    syncTimer(null);
    setElapsed(0);
    setDescription("");
    setShowSubmitModal(true);
  };

  const handleSubmit = async () => {
    if (!description.trim()) return;
    const ref = newRef();
    const payload = {
      machine_name: pendingEntry.machine,
      site: pendingEntry.site,
      work_date: localDate(),
      hours: pendingEntry.hours,
      description: description.trim(),
      client_ref: ref,
    };
    if (authCtx?.user?.id) payload.user_id = authCtx.user.id;
    await send(`${API}/time/log`, { kind: "hours", meta: { ref }, body: payload });
    setPendingEntry(null);
    clearKey("timer_pending");
    setShowSubmitModal(false);
    setDescription("");
    loadEntries(entryRange);
    cachedJSON(`${API}/time/suggestions`).then(d => setMachines(d.machines || [])).catch(() => {});
  };

  const handleDiscard = () => {
    setPendingEntry(null);
    clearKey("timer_pending");
    setShowSubmitModal(false);
    setDescription("");
  };

  const handleDelete = async (id) => {
    if (remove(i => i.kind === "hours" && i.meta?.ref === id)) return;
    const r = await fetch(`${API}/time/${id}`, { method: "DELETE", credentials: "include" }).catch(() => null);
    if (!r || !r.ok) {
      const detail = r ? (await r.json().catch(() => ({}))).detail : null;
      alert(detail || "Kustutamine ebaõnnestus");
      return;
    }
    setEntries(prev => prev.filter(e => e.id !== id));
  };

  const handleUpdate = async (id, updates) => {
    await fetch(`${API}/time/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(updates),
    });
    loadEntries(entryRange);
  };

  const isAdmin = authCtx?.user?.role === "admin";
  const canLogForOthers = isAdmin || authCtx?.user?.role === "site_lead";

  const handleAddNewMachine = () => {
    if (!newMachineName.trim()) return;
    const name = newMachineName.trim();
    if (!machines.includes(name)) setMachines(prev => [...prev, name]);
    setSelectedMachine(name);
    setShowNewMachine(false);
    setNewMachineName("");
  };

  const canStart = selectedMachine && selectedSite && !timerState?.running && !pendingEntry;
  const isRunning = timerState?.running;

  const totalHours = shownEntries.reduce((s, e) => s + (Number(e.hours) || 0), 0);

  const inputBg = dark ? "#1a1e27" : "#f0f1f4";
  const inputBorder = dark ? "#2b303c" : "#d0d3da";
  const inputColor = dark ? "#e8eaef" : "#1a1f29";

  return (
    <div style={{ background: t.bg, minHeight: themeCtx ? undefined : "100vh", color: t.text, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      {!themeCtx && (
        <div style={{ background: NAVY, borderBottom: `2px solid ${ACCENT}`, padding: "12px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <span style={{ color: "#fff", fontWeight: 800, fontSize: 16, letterSpacing: 2 }}>STEINMANN OÜ</span>
            <span style={{ color: "#ffffff44", margin: "0 10px" }}>|</span>
            <span style={{ color: "#ffffff99", fontWeight: 600, fontSize: 14 }}>MASINATUNNID</span>
          </div>
          <div style={{ color: ACCENT, fontWeight: 800, fontSize: 18 }}>{totalHours.toFixed(1)} h</div>
        </div>
      )}

      <div style={{ maxWidth: 520, margin: "0 auto", padding: "24px 16px" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontSize: isRunning ? 56 : 48, fontWeight: 800, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", color: isRunning ? ACCENT : t.muted, letterSpacing: 2 }}>
            {isRunning ? fmtElapsed(elapsed) : "00:00:00"}
          </div>
          {isRunning && (
            <div style={{ color: t.muted, fontSize: 13, marginTop: 8 }}>
              {timerState.machine} · {timerState.site}
            </div>
          )}
          {pendingEntry && !showSubmitModal && (
            <div style={{ color: t.error || "#c0392b", fontSize: 13, marginTop: 8, fontWeight: 600 }}>
              Esita eelmine kirje enne uue alustamist
            </div>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "center", marginBottom: 32 }}>
          {isRunning ? (
            <button onClick={handleStop} style={{
              width: 80, height: 80, borderRadius: "50%", border: "none", background: "#c0392b",
              color: "#fff", fontSize: 28, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 4px 20px rgba(192,57,43,0.4)",
            }}>
              <div style={{ width: 24, height: 24, background: "#fff", borderRadius: 3 }} />
            </button>
          ) : (
            <button onClick={handleStart} disabled={!canStart} style={{
              width: 80, height: 80, borderRadius: "50%", border: "none",
              background: canStart ? ACCENT : (dark ? "#2b303c" : "#d0d3da"),
              color: canStart ? "#fff" : (dark ? "#555" : "#999"),
              fontSize: 28, cursor: canStart ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: canStart ? "0 4px 20px rgba(47,111,237,0.4)" : "none",
            }}>
              <div style={{ width: 0, height: 0, borderTop: "14px solid transparent", borderBottom: "14px solid transparent", borderLeft: "24px solid currentColor", marginLeft: 4 }} />
            </button>
          )}
        </div>

        {!isRunning && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 32 }}>
            <div>
              <div style={{ fontSize: 11, color: t.muted, letterSpacing: 1, marginBottom: 6, fontWeight: 700 }}>MASIN</div>
              {showNewMachine ? (
                <div style={{ display: "flex", gap: 8 }}>
                  <input value={newMachineName} onChange={e => setNewMachineName(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleAddNewMachine()}
                    placeholder="Masina nimi" autoFocus
                    style={{ flex: 1, background: inputBg, border: `1px solid ${inputBorder}`, borderRadius: 8, padding: "10px 12px", color: inputColor, fontSize: 15, outline: "none" }} />
                  <button onClick={handleAddNewMachine} style={{ background: ACCENT, border: "none", borderRadius: 8, color: "#fff", fontWeight: 700, padding: "10px 16px", cursor: "pointer" }}>OK</button>
                  <button onClick={() => setShowNewMachine(false)} style={{ background: dark ? "#333" : "#ddd", border: "none", borderRadius: 8, color: t.text, fontWeight: 700, padding: "10px 12px", cursor: "pointer" }}>×</button>
                </div>
              ) : (
                <select value={selectedMachine} onChange={e => {
                  if (e.target.value === "__new__") { setShowNewMachine(true); return; }
                  setSelectedMachine(e.target.value);
                }} style={{
                  width: "100%", background: inputBg, border: `1px solid ${inputBorder}`, borderRadius: 8,
                  padding: "10px 12px", color: inputColor, fontSize: 15, outline: "none", appearance: "auto",
                }}>
                  <option value="">— Vali masin —</option>
                  <option value="Käsitöö">Käsitöö (ilma masinata)</option>
                  {machines.map(m => <option key={m} value={m}>{m}</option>)}
                  <option value="__new__">+ Lisa uus masin...</option>
                </select>
              )}
            </div>

            <div>
              <div style={{ fontSize: 11, color: t.muted, letterSpacing: 1, marginBottom: 6, fontWeight: 700 }}>OBJEKT</div>
              <select value={selectedSite} onChange={e => setSelectedSite(e.target.value)} style={{
                width: "100%", background: inputBg, border: `1px solid ${inputBorder}`, borderRadius: 8,
                padding: "10px 12px", color: inputColor, fontSize: 15, outline: "none", appearance: "auto",
              }}>
                <option value="">— Vali objekt —</option>
                {sites.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
              </select>
            </div>
          </div>
        )}

        {canLogForOthers && (
          <ManualHoursForm t={t} dark={dark} machines={machines} sites={sites}
            onSaved={() => loadEntries(entryRange)}
            inputStyle={{
              width: "100%", boxSizing: "border-box", background: inputBg, border: `1px solid ${inputBorder}`,
              borderRadius: 8, padding: "10px 12px", color: inputColor, fontSize: 15, outline: "none", appearance: "auto",
            }} />
        )}

        <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ display: "flex", gap: 4, background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 8, padding: 3 }}>
              {[["today", "Täna"], ["week", "Nädal"], ["month", "Kuu"]].map(([key, label]) => (
                <button key={key} onClick={() => setEntryRange(key)} style={{
                  padding: "6px 12px", border: "none", borderRadius: 6, fontSize: 12, fontWeight: entryRange === key ? 600 : 500,
                  background: entryRange === key ? (dark ? "#fff" : "#fff") : "transparent",
                  color: entryRange === key ? NAVY : t.muted,
                  cursor: "pointer", fontFamily: "inherit",
                }}>{label}</button>
              ))}
            </div>
            <div style={{ fontSize: 15, fontWeight: 800, color: ACCENT }}>{totalHours.toFixed(1)} h</div>
          </div>

          {canLogForOthers && entryRange === "today" && <RunningTimers t={t} dark={dark} />}

          {shownEntries.length === 0 && (
            <div style={{ textAlign: "center", color: t.mutedLight, fontSize: 14, padding: "40px 0" }}>
              {entryRange === "today" ? "Tänaseid kirjeid ei ole." : "Kirjeid ei leitud."}
            </div>
          )}

          {(() => {
            if (entryRange === "today") {
              return shownEntries.map(e => <EntryCard key={e.id} e={e} t={t} dark={dark} onDelete={handleDelete} isAdmin={isAdmin} onUpdate={handleUpdate} />);
            }
            const byDate = {};
            for (const e of shownEntries) {
              const d = e.work_date;
              if (!byDate[d]) byDate[d] = [];
              byDate[d].push(e);
            }
            return Object.entries(byDate).map(([d, items]) => {
              const dayTotal = items.reduce((s, e) => s + (e.hours || 0), 0);
              const parts = d.split("-");
              const label = `${parts[2]}.${parts[1]}.${parts[0]}`;
              return (
                <div key={d} style={{ marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: t.muted, letterSpacing: 0.5 }}>{label}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: t.mutedLight }}>{dayTotal.toFixed(1)} h</div>
                  </div>
                  {items.map(e => <EntryCard key={e.id} e={e} t={t} dark={dark} onDelete={handleDelete} isAdmin={isAdmin} onUpdate={handleUpdate} />)}
                </div>
              );
            });
          })()}
        </div>
      </div>

      {showSubmitModal && pendingEntry && (
        <div style={{ position: "fixed", inset: 0, background: "#000000cc", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, zIndex: 100 }}>
          <div style={{ background: t.panel, border: `1px solid ${t.border}`, borderRadius: 16, padding: 28, width: "100%", maxWidth: 400 }}>
            <div style={{ fontWeight: 800, fontSize: 18, color: ACCENT, marginBottom: 20 }}>Logi tööaeg</div>

            <div style={{ display: "flex", gap: 20, marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 11, color: t.muted, letterSpacing: 1 }}>AEG</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: t.text }}>{pendingEntry.hours.toFixed(2)} h</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: t.muted, letterSpacing: 1 }}>MASIN</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: t.text }}>{pendingEntry.machine}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: t.muted, letterSpacing: 1 }}>OBJEKT</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: t.text }}>{pendingEntry.site}</div>
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: t.muted, letterSpacing: 1, marginBottom: 6 }}>KIRJELDUS *</div>
              <textarea value={description} onChange={e => setDescription(e.target.value)}
                placeholder="Mis tööd tegid?"
                rows={3}
                autoFocus
                style={{
                  width: "100%", boxSizing: "border-box", background: inputBg, border: `1px solid ${inputBorder}`,
                  borderRadius: 8, padding: "10px 12px", color: inputColor, fontSize: 14, outline: "none",
                  resize: "vertical", fontFamily: "inherit",
                }} />
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={handleDiscard} style={{
                flex: 1, padding: "14px", background: dark ? "#333" : "#e0e0e0", border: "none", borderRadius: 10,
                color: t.text, fontWeight: 700, cursor: "pointer", fontSize: 15,
              }}>Tühista</button>
              <button onClick={handleSubmit} disabled={!description.trim()} style={{
                flex: 2, padding: "14px", background: description.trim() ? ACCENT : (dark ? "#2b303c" : "#d0d3da"),
                border: "none", borderRadius: 10, color: "#fff", fontWeight: 800, cursor: description.trim() ? "pointer" : "not-allowed", fontSize: 15,
              }}>Salvesta</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
